<template>
  <div class="cart-summary critical-component-clear">
    <cart-freebies class="cart-summary__freebies" />

    <form
      class="cart-summary__form"
      :action="`${$variable('routes.cart.url')}`"
      method="post"
    >
      <div class="cart-summary__total">
        <div v-if="totalSavings > 0" class="cart-summary__saved">
          <p
            class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-text="$string('cart.summary.saved_label')"
          />

          <p
            class="text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold cart-summary__saved--discount"
            v-text="$formatMoney(totalSavings)"
          />
        </div>
      </div>

      <notification-panel
        v-if="limit && cart.items.length >= limit"
        class="cart-summary__notification"
        :show-close="false"
        :text="$string('cart.notifications.too_many_items')"
        type="error"
      />

      <notification-panel
        v-if="getCartItemLimitReached || getCartProductLimitReached"
        class="cart-summary__notification"
        :show-close="false"
        :title="$string('cart.notifications.cart_limit.title_html')"
        :text="cartLimitText"
        type="error"
      />

      <input
        v-if="activeCampaign"
        name="discount"
        type="hidden"
        :value="discountCode?.toLowerCase()"
      />

      <div class="cart-summary__button">
        <btn
          class="cart-summary__submit"
          :label="cartButton"
          name="checkout"
          :show-state-icon="
            cartStatus.label === 'updating' ||
            bundleItemUpdate.length > 0 ||
            depositItemUpdate.length > 0
          "
          :disabled="
            cartStatus.label === 'updating' ||
            bundleItemUpdate.length > 0 ||
            depositItemUpdate.length > 0
          "
          type="submit"
          @click="handleCheckoutClickEvent"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>

      <slot name="payments"></slot>
    </form>
  </div>
</template>

<script>
/**
 * Vue: Cart summary (cart-summary)
 * -----------------------------------------------------------------------------
 * Summary of cart metrics with checkout CTA.
 *
 * @param {Number} [limit] - Line item limit to display notification panel at.
 *
 */
import { mapState } from 'pinia';
import { Splide } from '@splidejs/splide';

import Btn from '~global/btn/btn';
import CartFreebies from '~async/cart-freebies/cart-freebies';
import NotificationPanel from '~async/utils/notification-panel/notification-panel';

import { formatTrackingItem } from '~/format/format-tracking-item';
import { computeDiscountLogic } from '~/helpers/vue';

import IconLoading from '~icons/general/loading.svg';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';

export default {
  name: 'CartSummary',

  components: {
    Btn,
    CartFreebies,
    NotificationPanel,
    IconLoading,
  },

  props: {
    limit: {
      type: Number,
      default: 20,
    },
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      getCartItemLimitReached: 'getCartItemLimitReached',
      getCartProductLimitReached: 'getCartProductLimitReached',
      activeCampaign: 'active',
      discountCode: 'code',
    }),

    ...mapState(useCartStore, {
      cart: 'response',
      cartStatus: 'status',
      bundleItemUpdate: 'bundleItemUpdate',
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * computing the button text as it includes the price
     */
    cartButton() {
      return this.$string('cart.checkout', {
        replace: {
          amount: this.$formatMoney(this.cart.total_price),
        },
      });
    },

    cartLimitText() {
      let text = '';

      if (this.getCartItemLimitReached) {
        text += this.$string('cart.notifications.cart_limit.text_items', {
          replace: {
            count: this.$variable('product.maxLineItems'),
          },
        });
      }
      if (this.getCartProductLimitReached) {
        text += this.$string('cart.notifications.cart_limit.text_products', {
          replace: {
            productCount: this.$variable('product.maxCartProducts'),
          },
        });
      }
      return text;
    },

    /**
     * computes the savings information out of price and compare_at_price
     * as we need to include outlet products manually here. Set price to 0 for freebies
     */
    totalSavings() {
      const getItemPrices = this.cart.items.map((item) => ({
        price: parseInt(item.final_price) === 0 ? 0 : item.price,
        compare_at_price:
          parseInt(item.final_price) === 0 ? 0 : item.compare_at_price,
        quantity: item.quantity,
      }));

      /**
       * we get the prices here and calculate the total price by multiplying the price with the quantity
       * we take compare_at_price if it exists, otherwise we take the price
       */
      const totalPrice = getItemPrices.reduce((total, item) => {
        const effectivePrice = item.compare_at_price ?? item.price;
        return total + effectivePrice * item.quantity;
      }, 0);

      return totalPrice - this.cart.total_price;
    },
  },

  mounted() {
    const paymentSlider = document.querySelector('[js-payment-slider]');

    if (paymentSlider) {
      const carousel = new Splide(paymentSlider, {
        arrows: false,
        drag: true,
        focus: 'left',
        gap: 'var(--spacing-xs)',
        pagination: false,
        autoWidth: true,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
      });

      carousel.mount();
    }
  },

  methods: {
    /**
     * Handle Checkout button click event
     */
    handleCheckoutClickEvent() {
      cnvs.EventBus.emit('dataLayer:begin_checkout', {
        event: 'begin_checkout',
        currency: this.cart.currency,
        value: this.cart.total_price,
        coupon: this.cart.discount_codes
          ? this.cart.discount_codes.map((item) => item.code).join(', ')
          : '-',
        items: this.cart.items.map((item) => {
          const price = item.price;
          const compareAtPrice = item.compare_at_price;
          const discountLogic = computeDiscountLogic({
            discountPreview: false,
            onSale: compareAtPrice > price,
            product: item.product,
            variant: item,
          });
          const percentageDiscount = this.getCampaignDiscount(
            discountLogic,
            'active',
          );
          const discountObject = {
            percentageDiscount,
            discountCode: this.discountCode,
          };
          const deliveryTime = this.$string('product.delivery_time', {
            replace: { time: this.$variable('product.deliveryTime') },
          });
          const currencyCode = this.$variable('store.currency');

          return formatTrackingItem({
            product: item.product,
            variant: item,
            template: 'Cart Drawer',
            discountObject,
            deliveryTime,
            currencyCode,
            quantity: 1,
          });
        }),
      });
    },
  },
};
</script>

<style lang="scss">
@import './cart-summary';
</style>
